/**
 * «Занятие вживую», slice 1 (docs/design/live-lesson.md): the header on
 * the lesson's sheet, «в прошлый раз N» on each skill, and a copy in the
 * browser of a draft the server has not got yet. Pure, so it is tested
 * without a browser (scripts/verify/live-lesson.ts).
 */
import { minutesLeft, progress } from './today-card';

/** The part of localStorage used here, so a test can hand in its own. */
export interface StoreLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** localStorage, or null where the webview refuses it. */
export function browserStore(): StoreLike | null {
  try { return window.localStorage; } catch { return null; }
}

export const rateKey = (bookingId: string) => `cb.draft.rate.${bookingId}`;
export const groupKey = (sessionId: string) => `cb.draft.group.${sessionId}`;

/** A copy older than this is from a lesson long gone: dropped, not restored. */
export const LOCAL_MAX_AGE = 3 * 24 * 3600_000;

/**
 * Kept while a change waits to reach the server, so a Telegram killed
 * offline loses nothing. Storage that throws (private mode, full) is
 * ignored: the sheet works as before without it.
 */
export function keepLocal(store: StoreLike | null, key: string, json: string, now: number) {
  try { store?.setItem(key, JSON.stringify({ json, at: now })); } catch { /* no copy this time */ }
}

export function dropLocal(store: StoreLike | null, key: string) {
  try { store?.removeItem(key); } catch { /* nothing to drop */ }
}

export function readLocal(store: StoreLike | null, key: string, now: number): { json: string; at: number } | null {
  let raw: string | null = null;
  try { raw = store?.getItem(key) ?? null; } catch { return null; }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { json?: unknown; at?: unknown };
    if (typeof v.json !== 'string' || typeof v.at !== 'number') return null;
    if (now - v.at > LOCAL_MAX_AGE) { dropLocal(store, key); return null; }
    return { json: v.json, at: v.at };
  } catch { return null; }
}

/**
 * The local copy, unless the server holds a draft saved after it (typed
 * on another device since). The copy exists only while unsent, so
 * otherwise it is the newer one.
 */
export function restoreFrom(local: { json: string; at: number } | null, serverDraftAt: string | null): string | null {
  if (!local) return null;
  if (serverDraftAt && new Date(serverDraftAt).getTime() > local.at) return null;
  return local.json;
}

/** /last-scores rows as axis → score; only real scores, 1 to 10. */
export function lastScores(rows: { axis_id: string; score: number }[] | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows ?? []) {
    const n = Number(r.score);
    if (Number.isInteger(n) && n >= 1 && n <= 10) out[r.axis_id] = n;
  }
  return out;
}

/**
 * «в прошлый раз 6», tapped: that skill takes 6. Every other skill stays
 * where it is — 0 is "not trained today" (909155b), so last time is a
 * hint, never a starting point.
 */
export function takeLast(scores: Record<string, number>, axisId: string, last: number | undefined) {
  if (last === undefined || scores[axisId] === last) return scores;
  return { ...scores, [axisId]: last };
}

/** What the header shows: on court with N minutes left, over, or not started. */
export function liveHeader(startsAt: string, endsAt: string, now: number) {
  const state = now < new Date(startsAt).getTime() ? 'soon' as const
    : now < new Date(endsAt).getTime() ? 'on' as const : 'over' as const;
  return { state, left: minutesLeft(endsAt, now), progress: progress({ starts_at: startsAt, ends_at: endsAt }, now) };
}
